// shared/terrain.ts — terrain API and daily puzzle metadata.
// Shared between server (validation) and client (rendering + live scoring).
//
// The actual terrain *generation* lives server-side in server/terrainGen.ts.
// This module only knows how to *sample* a heightmap that's already been
// generated and shipped to the caller. The client receives the heightmap as
// part of /api/puzzle/today.
//
// Coordinate convention: world units are *heightmap cells*. The "gameplay
// grid" and the heightmap grid are the same grid — point (x, y) with
// integer values addresses the heightmap cell directly, with no scale
// factor in between. heightAt(x, y) is a plain bilerp on that grid.
//
// Physical scale: one world unit corresponds to WU_TO_KM kilometers on the
// ground. This conversion is applied in scoring (where physics math wants
// km) but never affects raw coordinate arithmetic.

// v2 dimensions. 300x150 = 45,000 cells. As Uint16 that's 90 KB raw / ~120 KB
// base64 — slightly SMALLER on the wire than the old 200x120 Float32 map,
// which was 96 KB / ~128 KB. The extra width is what makes room for several
// north-south ranges with usable gaps between them.
export const TOWN_NAMES = ["North Haverbrook", "Rockmont", "Schnectadectady",
  "Spoutin' Devuil", "New Rome", "New Boots", "Fresh Kills", "Kill Devil Hills",
  "Vaduz", "Poughkeepsie", "Fort Branson", "Fort Terry", "Squim", "Untenable Bluffs",
  "Suisun Hollow", "Dorf", "St. Tiffany", "San Quentin", "Old Saybrook", "Innsmouth",
  "Index, WA", "Fort Van Buren", "Rolle", "Old Rome", "Guadalajara", "Brookfield", "Springfield",
  "Port Elsewhere", "Port Mercator", "Port Pippin", "Stratton-Upon-Oakmont", "Windermere-On-Sea",
  "Socorro", "Los Nopales", "San Lorenzo", "Yountstown", "South Long Beach", "Southeast Detroit",
  "Baahabah, ME", "Moose Jaw", "Medicine Hat", "Lethbridge", "St. Glencore", "Rio Dorado",
  "Green River", "New Bombay", "Gotham", "Nollywood", "Twenty-Three Mile Camp", "151 Mile House",
  "Chengdu 成都", "Harbin 哈尔滨", "Dongguan 东莞", "Santa Fe", "Saint Loy", "Las Vacas", "Ballwin, MO",
  "Big Timber", "Oubliette", "Garden Spot", "Cormorant Bluffs", "Den Haag", "Y’ha-nthlei", "Orinda",
  "Townsville, QLD", "Wigan Pier", "Alert, NU", "Luanshi Mowang 乱世魔王", "New Valletta", "San Jose",
  "Nowhere", "Dis", "Paradise, NV", "Pahrump", "Tonopah", "Daluan 大乱", "Champ, MO", "Utopia",
  "Changchun 长春", "E.P.C.O.Y.", "Saskatoon", "Norilsk", "Iquitos", "Iqaluit", "The Nameless City",
  "St. Caligula", "Grote Gracht", "Davos", "Arroyo Hundido", "St. Ives", "Ketterlenny",
  "A Coruña", "Windhoek", "Addis Ababa", "Da Nang", "Kuala Lumpur", "Land's End", "Shelbyville",
  "Bishop and Rook", "Thor's Haven", "Mammoth Springs", "Hope Springs, ETL", "Walvis Bay",
  "Klaksvik", "Ravenna, NCP", "Thiruvananthapuram", "Domus Nova", "Starry Happy, RS", "Krk, HR"];
export const MAP_W = 300;
export const MAP_H = 150;
export const WATER_THRESHOLD = 0.22;
export const HEIGHT_SCALE_KM = 2.0;
/**
 * Physical scale: kilometers per world unit. The map is MAP_W × MAP_H
 * world units (= heightmap cells), so the physical map size is
 * (MAP_W * WU_TO_KM) × (MAP_H * WU_TO_KM) kilometers.
 */
export const WU_TO_KM = 0.5;

// A TerrainParams is the heightmap blob. Its (width, height) must equal
// (MAP_W, MAP_H); the server is responsible for generating heightmaps at
// that exact resolution. We carry width/height redundantly only for
// defensive index math (callers don't have to know MAP_W).
export type TerrainParams = {
  /** Row-major heightmap, length = width * height, values in [0, 1]. */
  heightmap: Float32Array | number[];
  /** Number of columns in the heightmap. Equals MAP_W. */
  width: number;
  /** Number of rows in the heightmap. Equals MAP_H. */
  height: number;
};

/**
 * Sample the heightmap at world coordinates (x in [0, MAP_W], y in [0, MAP_H])
 * via bilinear interpolation. Clamps to the map edge. Integer (x, y) hits
 * a heightmap cell corner exactly.
 *
 * Returns a value in [0, 1] (modulo the rare case where a callsite passes a
 * heightmap that hasn't been normalized — generateHeightmap() does normalize).
 */
export function heightAt(x: number, y: number, params: TerrainParams): number {
  const { heightmap, width, height } = params;
  // World coords ARE heightmap coords now; no scaling required.
  // Clamp to the last sample-able cell (width-1, height-1) so that the
  // ix+1 / iy+1 lookups stay in bounds.
  let hx = x, hy = y;
  if (hx < 0) hx = 0; else if (hx > width - 1) hx = width - 1;
  if (hy < 0) hy = 0; else if (hy > height - 1) hy = height - 1;

  const ix = Math.floor(hx);
  const iy = Math.floor(hy);
  const ix1 = ix + 1 < width ? ix + 1 : ix;
  const iy1 = iy + 1 < height ? iy + 1 : iy;
  const u = hx - ix;
  const v = hy - iy;

  const h00 = heightmap[iy * width + ix];
  const h10 = heightmap[iy * width + ix1];
  const h01 = heightmap[iy1 * width + ix];
  const h11 = heightmap[iy1 * width + ix1];

  return (
    h00 * (1 - u) * (1 - v) +
    h10 * u * (1 - v) +
    h01 * (1 - u) * v +
    h11 * u * v
  );
}

/**
 * Is the given world coordinate over the natural ocean (i.e., heightmap
 * below WATER_THRESHOLD)? Rivers and lakes are NOT considered water tiles
 * — they're geometric features tested separately by the road analyzer.
 */
export function isWater(x: number, y: number, params: TerrainParams): boolean {
  return heightAt(x, y, params) < WATER_THRESHOLD;
}

// --- Heightmap wire codec (v2) -------------------------------------------
//
// v1 shipped raw Float32 bytes. Float32 carries ~7 significant digits for a
// value that only ever spans one map's relief, which is vastly more precision
// than the terrain has meaning at — the DP that consumes it quantises to
// 1600 levels anyway.
//
// v2 quantises to Uint16 over the map's ACTUAL min/max rather than a nominal
// 0..1. That halves the bytes and, because the real span is usually a
// fraction of the nominal one, the effective precision goes UP: 65,535 steps
// across the observed relief instead of across a range mostly unused.
//
// This is a wire-format change, which is why the blob carries a version.

export type HeightmapCodec = { b64: string; min: number; max: number };

export function encodeHeightmapU16(data: Float32Array): HeightmapCodec {
  let min = Infinity, max = -Infinity;
  for (let i = 0; i < data.length; i++) {
    if (data[i] < min) min = data[i];
    if (data[i] > max) max = data[i];
  }
  const span = max - min;
  const q = new Uint16Array(data.length);
  if (span > 0) {
    const k = 65535 / span;
    // Math.round, not truncation: truncating biases every cell downward by
    // half a step, which tilts the whole map and shifts the waterline.
    for (let i = 0; i < data.length; i++) q[i] = Math.round((data[i] - min) * k);
  }
  return { b64: bytesToBase64(new Uint8Array(q.buffer)), min, max };
}

export function decodeHeightmapU16(c: HeightmapCodec): Float32Array {
  const bin = atob(c.b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  const q = new Uint16Array(u8.buffer);
  const out = new Float32Array(q.length);
  const k = (c.max - c.min) / 65535;
  for (let i = 0; i < q.length; i++) out[i] = c.min + q[i] * k;
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  let s = "";
  // Chunked: String.fromCharCode(...bytes) blows the argument limit well
  // before 90,000 bytes.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(s);
}

// --- Ridge skeleton (debug payload) --------------------------------------
//
// Coordinates are in world space (= heightmap cells). Generated server-side
// alongside the heightmap (see server/terrainGen.ts) and shipped to the
// client as an optional payload for debug visualization.

export type SkeletonPeak = { x: number; y: number };
export type SkeletonEdge = {
  ax: number; ay: number;
  bx: number; by: number;
  weight: number;
};
export type Skeleton = {
  trunk: Array<{ x: number; y: number }>;  // polyline along trunk centerline
  peaks: SkeletonPeak[];
  edges: SkeletonEdge[];
};

/**
 * A river polyline traced from the drainage network. Each node knows its
 * throughflow — total upstream area contributing water at that point — so
 * the client can render width proportional to flow magnitude. Polyline is
 * ordered headwater → mouth; the implicit "next" link follows array order
 * (a doubly-linked list isn't needed on the wire since the polyline is
 * already a sequence).
 *
 * Coordinates are in world (= heightmap) cells.
 */
export type River = {
  /** Polyline points, ordered headwater → mouth. */
  points: Array<{ x: number; y: number }>;
  /** Per-point throughflow (upstream drainage in cells). */
  throughflow: number[];
};

/**
 * A lake: a connected region of cells sharing a flat water surface.
 * Lakes have their own surface elevation independent of the underlying
 * heightmap; bathymetry (depth) at a cell is `surface − heightmap[cell]`.
 * Endorheic lakes have `outflow === 0`; lakes with a spillway feed a
 * downstream river of the same throughflow.
 *
 * `outline` is one or more closed polylines tracing the lake's shoreline.
 * The outer boundary is one loop; any islands (lake cells surrounding a
 * non-lake cell) appear as additional loops. Each loop's first and last
 * points are identical (closing the loop). Use the outline for crisp shore
 * rendering — the cell set itself produces a stair-step boundary.
 */
export type Lake = {
  /** Cells composing the lake, in world (= heightmap) coordinates. */
  cells: Array<{ x: number; y: number }>;
  /** Lake-surface elevation in normalized [0, 1] heightmap units. */
  surface: number;
  /** Total water leaving the lake (cells of upstream area); 0 if endorheic. */
  outflow: number;
  /** Closed shore polylines (1 outer + any island loops). World coords. */
  outline: Array<Array<{ x: number; y: number }>>;
};

// --- Daily puzzle ---------------------------------------------------------
//
// Note: dailyPuzzle() no longer carries TerrainParams. Terrain is generated
// server-side (see server/terrainGen.ts) and merged in before the puzzle is
// shipped to the client. Only the client-safe bits live here: seed, towns,
// and the existing road.

export type Town = { x: number; y: number; name: string };
export type Puzzle = {
  seed: number;
  params: TerrainParams;
  towns: Town[];
  existingRoad: Array<{ x: number; y: number }>;
  /** Drainage polylines used for geometric bridge detection. May be empty
   *  during early loading; defaults to []. */
  rivers: River[];
  /** Lake polygons (outlines) used for geometric bridge detection. May
   *  be empty during early loading; defaults to []. */
  lakes: Lake[];
};

/** Just the metadata — the server fills in `params`, `rivers`, `lakes`
 *  after terrain generation. */
export type PuzzleMeta = Omit<Puzzle, "params" | "rivers" | "lakes">;

export function seedFromDate(date: string): number {
  let h = 0;
  for (let i = 0; i < date.length; i++) {
    h = (h * 31 + date.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 1_000_000;
}

/**
 * Build the puzzle metadata for a given date. This does NOT generate the
 * heightmap; the server attaches that separately. The client uses this same
 * function on the response payload only to validate seeds match.
 */
export function dailyPuzzleMeta(date: string): PuzzleMeta {
  const seed = seedFromDate(date);
  // Coordinates are in world units (heightmap cells). All hardcoded
  // positions here were chosen at the old 100×60 grid; they're scaled by
  // 2 to keep the same physical placement on the new 200×120 grid.
  // Expressed as fractions of the map so a dimension change doesn't silently
  // move towns off the terrain. The old absolute coordinates were written for
  // a 200x120 grid: at 300x150, townB's x=176 landed in the middle of the map
  // instead of near the right edge, with two mountain ranges east of it.
  //
  // Both towns sit outside the range band (ranges span 0.12..0.88 of the
  // width), so the straight line between them crosses every range. Otherwise
  // some seeds would place a town past the last wall and hand the player a
  // route with nothing to decide.
  const [townA_name, townB_name] = [...TOWN_NAMES].sort(() => 0.5 - Math.random());

  const townA: Town = {
    x: Math.round(MAP_W * (0.035 + 0.03 * ((seed % 14) / 14))),
    y: Math.round(MAP_H * (0.38 + 0.24 * ((seed % 22) / 22))),
    name: townA_name,
  };
  const townB: Town = {
    x: Math.round(MAP_W * (0.935 - 0.03 * (((seed >> 3) % 14) / 14))),
    y: Math.round(MAP_H * (0.38 + 0.24 * (((seed >> 5) % 18) / 18))),
    name: townB_name,
  };
  const existingRoad = [
    townA,
    { x: 36, y: 104 }, { x: 56, y: 100 }, { x: 72, y: 88 },
    { x: 80, y: 72 },  { x: 76, y: 56 },  { x: 64, y: 40 },
    { x: 60, y: 24 },  { x: 76, y: 12 },  { x: 100, y: 8 },
    { x: 124, y: 12 }, { x: 144, y: 20 }, { x: 164, y: 24 },
    townB,
  ];
  return { seed, towns: [townA, townB], existingRoad };
}
